












































































































































































































































































































































































































import { Component, Watch } from 'vue-property-decorator';
import CompanyMixin from '@/mixins/CompanyMixin';
import user from '@/store/modules/user';
import { ticSearchCompany, ticGetFinancialReportFile } from '@/store/api';
import { financialFileIdsFromUrl } from '@/utils/ticFinancialDocument';

function extractTicCompanyId(search: any): number | undefined {
  const hits = search && search.hits;
  if (!Array.isArray(hits) || hits.length === 0) {
    return undefined;
  }
  const doc = hits[0] && hits[0].document;
  const id = doc && doc.companyId;
  if (typeof id === 'number' && !Number.isNaN(id)) {
    return id;
  }
  if (typeof id === 'string') {
    const n = parseInt(id, 10);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function extractTicDocument(search: any): any | null {
  const hits = search && search.hits;
  if (!Array.isArray(hits) || hits.length === 0) {
    return null;
  }
  const doc = hits[0] && hits[0].document;
  return doc && typeof doc === 'object' ? doc : null;
}

/** TIC financial ratios are 0–1 in API payloads; format for Swedish UI. */
function ticFormatRatioPercent(ratio: unknown): string | null {
  if (typeof ratio !== 'number' || Number.isNaN(ratio)) {
    return null;
  }
  return new Intl.NumberFormat('sv-SE', {
    style: 'percent',
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(ratio);
}

@Component
export default class CardTic extends CompanyMixin {
  loading = false;
  loadError: string | null = null;
  companyId: number | null = null;
  ticDocument: any | null = null;
  /** All `documents[]` entries from TIC search (årsredovisning, bolagsordning, ärenden, …). */
  ticDocuments: any[] = [];

  get displayCompanyName(): string {
    if (this.ticDocument && Array.isArray(this.ticDocument.names)) {
      const legal = this.ticDocument.names.find(
        (n: any) => n && n.companyNamingType === 'legalName',
      );
      if (legal && legal.nameOrIdentifier) {
        return legal.nameOrIdentifier;
      }
    }
    return this.overview ? this.overview.NAME : '';
  }

  get registeredOfficeLine(): string {
    const d = this.ticDocument;
    if (!d || !Array.isArray(d.registeredOffices) || d.registeredOffices.length === 0) {
      return '';
    }
    const o = d.registeredOffices[0];
    const parts = [o.municipality, o.county].filter(Boolean);
    return parts.join(', ');
  }

  get activityLine(): string {
    const d = this.ticDocument;
    if (!d) {
      return '';
    }
    if (d.isCeased === true) {
      return 'Upphört';
    }
    if (d.activityStatus) {
      return this.ticActivityStatusLabel(d.activityStatus);
    }
    return '';
  }

  get workplaceLine(): string {
    const d = this.ticDocument;
    if (!d || !Array.isArray(d.currentWorkplaces) || d.currentWorkplaces.length === 0) {
      return '';
    }
    return d.currentWorkplaces.map((w: any) => w.labelAddress).filter(Boolean).join('; ');
  }

  /** TIC `mostRecentSignatory.signatureDescription` — who may sign for the company. */
  get signatoryLine(): string {
    const s = this.ticDocument && this.ticDocument.mostRecentSignatory;
    const text = s && s.signatureDescription;
    if (typeof text !== 'string') {
      return '';
    }
    const t = text.trim();
    return t;
  }

  get primarySniLine(): string {
    const d = this.ticDocument;
    if (!d || !Array.isArray(d.sniCodes) || d.sniCodes.length === 0) {
      return '';
    }
    const sni = d.sniCodes[0];
    if (sni.sni_2025Code && sni.sni_2025Name) {
      return `${sni.sni_2025Code} — ${sni.sni_2025Name}`;
    }
    if (sni.sni_2007Code && sni.sni_2007Name) {
      return `${sni.sni_2007Code} — ${sni.sni_2007Name}`;
    }
    return '';
  }

  get turnoverLine(): string {
    const t = this.ticDocument && this.ticDocument.cTurnoverInterval;
    if (!t) {
      return '';
    }
    const a = t.categoryCodeDescription || '';
    const b = t.categoryCodeDescription2 || '';
    return b ? `${a} (${b})` : a;
  }

  get employeesLine(): string {
    const e = this.ticDocument && this.ticDocument.cNbrEmployeesInterval;
    return e && e.categoryCodeDescription ? e.categoryCodeDescription : '';
  }

  get vatLine(): string {
    const v = this.ticDocument && this.ticDocument.c_VATInterval;
    return v && v.categoryCodeDescription ? v.categoryCodeDescription : '';
  }

  get ownershipLine(): string {
    const d = this.ticDocument;
    if (!d || !d.cOwnership) {
      return '';
    }
    const parts = [d.cOwnership.categoryCodeDescription, d.cPrivPubl && d.cPrivPubl.categoryCodeDescription].filter(
      Boolean,
    );
    return parts.join(' · ');
  }

  get sectorDescription(): string {
    const s = this.ticDocument && this.ticDocument.cSector;
    return s && s.categoryCodeDescription ? s.categoryCodeDescription : '';
  }

  get shareCapitalLine(): string {
    const sc = this.ticDocument && this.ticDocument.mostRecentShareCapital;
    if (!sc) {
      return '';
    }
    const cur = sc.shareCapitalISO4217CurrencyCode || 'SEK';
    const amt = sc.shareCapitalAmount;
    if (amt == null) {
      return '';
    }
    return `${amt.toLocaleString('sv-SE')} ${cur} (${sc.numberOfShares != null ? sc.numberOfShares : '—'} aktier)`;
  }

  get financialSummaryPeriod(): string {
    const fs = this.ticDocument && this.ticDocument.mostRecentFinancialSummary;
    if (!fs) {
      return '';
    }
    const a = this.formatUnixSec(fs.periodStart);
    const b = this.formatUnixSec(fs.periodEnd);
    if (a && b) {
      return `${a} – ${b}`;
    }
    return '';
  }

  get financialSummaryFigures(): string {
    const fs = this.ticDocument && this.ticDocument.mostRecentFinancialSummary;
    if (!fs) {
      return '';
    }
    const parts: string[] = [];
    if (fs.rs_NetSalesK != null) {
      parts.push(`Nettoomsättning ${fs.rs_NetSalesK} tkr`);
    }
    if (fs.rs_OperatingProfitOrLossK != null) {
      parts.push(`Rörelseresultat ${fs.rs_OperatingProfitOrLossK} tkr`);
    }
    if (fs.rs_ProfitAfterFinancialItemsK != null) {
      parts.push(`Resultat efter finansiella poster ${fs.rs_ProfitAfterFinancialItemsK} tkr`);
    }
    const opM = ticFormatRatioPercent(fs.km_OperatingMargin);
    if (opM) {
      parts.push(`Rörelsemarginal ${opM}`);
    }
    const netM = ticFormatRatioPercent(fs.km_NetProfitMargin);
    if (netM) {
      parts.push(`Nettomarginal ${netM}`);
    }
    const sol = ticFormatRatioPercent(fs.km_EquityAssetsRatio);
    if (sol) {
      parts.push(`Soliditet ${sol}`);
    }
    if (fs.bs_TotalAssetsK != null) {
      parts.push(`Totala tillgångar ${fs.bs_TotalAssetsK} tkr`);
    }
    if (fs.fn_NumberOfEmployees != null) {
      parts.push(`Anställda ${fs.fn_NumberOfEmployees}`);
    }
    if (typeof fs.isAudited === 'boolean') {
      parts.push(fs.isAudited ? 'Revisionsberättelse: Ja' : 'Revisionsberättelse: Nej');
    }
    if (fs.software) {
      parts.push(fs.software);
    }
    return parts.join(' · ');
  }

  get emailsLine(): string[] {
    const d = this.ticDocument;
    if (!d || !Array.isArray(d.emailAddresses)) {
      return [];
    }
    return d.emailAddresses.map((e: any) => e.emailAddress).filter(Boolean);
  }

  get hyperlinksLine(): Array<{ label: string; url: string }> {
    const d = this.ticDocument;
    if (!d || !Array.isArray(d.hyperlinks)) {
      return [];
    }
    return d.hyperlinks
      .filter((h: any) => h && h.hyperlink)
      .map((h: any) => ({
        url: h.hyperlink,
        label: h.hyperlinkType || h.hyperlink,
      }));
  }

  /** TIC `intelligence` — risk signals (e.g. Skatteverket penalty fees). */
  get intelligenceEntries(): any[] {
    const d = this.ticDocument;
    if (!d || !Array.isArray(d.intelligence)) {
      return [];
    }
    return d.intelligence.filter((x: any) => x && typeof x === 'object');
  }

  /** TIC `registration` — F-skatt, moms, arbetsgivare; fallback to `isRegisteredFor*` flags. */
  get registrationRows(): Array<{ label: string; status: string; fromText: string }> {
    const d = this.ticDocument;
    if (!d) {
      return [];
    }
    const typeOrder = ['fTax', 'vat', 'payroll'] as const;
    const byType: Partial<Record<(typeof typeOrder)[number], any>> = {};
    if (Array.isArray(d.registration)) {
      for (const r of d.registration) {
        const t: unknown = r && r.companyRegistrationType;
        if (t === 'fTax' || t === 'vat' || t === 'payroll') {
          byType[t] = r;
        }
      }
    }
    const rows: Array<{ label: string; status: string; fromText: string }> = [];
    for (const t of typeOrder) {
      const r = byType[t];
      if (r && typeof r === 'object') {
        let status = '—';
        if (r.isRegistered === true) {
          status = 'Registrerad';
        } else if (r.isRegistered === false) {
          status = 'Ej registrerad';
        }
        const fromText =
          r.registeredFrom != null && typeof r.registeredFrom === 'number'
            ? this.formatUnixSec(r.registeredFrom)
            : '—';
        rows.push({
          label: this.ticRegistrationTypeLabel(t),
          status,
          fromText,
        });
        continue;
      }
      const boolKey =
        t === 'fTax'
          ? 'isRegisteredForFTax'
          : t === 'vat'
            ? 'isRegisteredForVAT'
            : 'isRegisteredForPayroll';
      const b = d[boolKey];
      if (b === true || b === false) {
        rows.push({
          label: this.ticRegistrationTypeLabel(t),
          status: b ? 'Registrerad' : 'Ej registrerad',
          fromText: '—',
        });
      }
    }
    return rows;
  }

  /** TIC `bankAccounts` — bankgiro/plusgiro (and other) payment account numbers. */
  get bankAccountRows(): Array<{ typeLabel: string; accountNumber: string }> {
    const d = this.ticDocument;
    if (!d || !Array.isArray(d.bankAccounts)) {
      return [];
    }
    const rows: Array<{ typeLabel: string; accountNumber: string }> = [];
    for (const b of d.bankAccounts) {
      if (!b || typeof b !== 'object') {
        continue;
      }
      const raw = b.accountNumber;
      if (raw == null || String(raw).trim() === '') {
        continue;
      }
      rows.push({
        typeLabel: this.ticBankAccountTypeLabel(b.bankAccountType),
        accountNumber: String(raw).trim(),
      });
    }
    return rows;
  }

  @Watch('overview', { immediate: true })
  onOverviewReady() {
    if (!this.overview) {
      return;
    }
    void this.loadTic();
  }

  @Watch('$route.params.OrgNr')
  onOrgNrChanged() {
    if (!this.overview) {
      return;
    }
    void this.loadTic();
  }

  formatStreetAddress(addr: any): string {
    if (!addr) {
      return '—';
    }
    const line1 = [addr.co, addr.streetAddress].filter(Boolean).join(', ');
    const line2 = [addr.postalCode, addr.city].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ') || '—';
  }

  formatUnixSec(sec: number | undefined): string {
    if (sec == null || typeof sec !== 'number') {
      return '—';
    }
    const ms = sec * 1000;
    const t = new Date(ms);
    if (Number.isNaN(t.getTime())) {
      return '—';
    }
    return t.toLocaleDateString('sv-SE');
  }

  documentTypeLabel(t: string | undefined): string {
    if (!t) {
      return '—';
    }
    const map: Record<string, string> = {
      annualReport: 'Årsredovisning',
      articlesOfAssociation: 'Bolagsordning',
      case: 'Ärende',
    };
    return map[t] || t;
  }

  /** TIC searchcompany `activityStatus` enum → Swedish label for Bolagsform / status line. */
  ticActivityStatusLabel(status: string | undefined): string {
    if (!status) {
      return '';
    }
    const map: Record<string, string> = {
      isActive: 'Aktiv',
      isCeased: 'Upphört',
    };
    return map[status] || status;
  }

  ticRegistrationTypeLabel(type: 'fTax' | 'vat' | 'payroll'): string {
    const map: Record<string, string> = {
      fTax: 'F-skatt',
      vat: 'Momsregistrering',
      payroll: 'Arbetsgivaravgifter',
    };
    return map[type] || type;
  }

  ticBankAccountTypeLabel(t: string | undefined): string {
    if (!t) {
      return '—';
    }
    const map: Record<string, string> = {
      bankgiro: 'Bankgiro',
      plusgiro: 'Plusgiro',
    };
    return map[t] || t;
  }

  ticIntelligenceTypeLabel(type: string | undefined): string {
    if (!type) {
      return '';
    }
    const map: Record<string, string> = {
      penaltyFees: 'Förseningsavgifter och viten',
    };
    return map[type] || type;
  }

  ticIntelligenceSubTypeLabel(sub: string | undefined): string {
    if (!sub) {
      return '';
    }
    const map: Record<string, string> = {
      PENALTY_FEE: 'Förseningsavgift',
    };
    return map[sub] || sub;
  }

  intelligenceHeading(entry: any): string {
    if (!entry) {
      return 'Observation';
    }
    const typePart = this.ticIntelligenceTypeLabel(entry.companyIntelligenceType);
    if (typePart) {
      return typePart;
    }
    const subPart = this.ticIntelligenceSubTypeLabel(entry.companyIntelligenceSubType);
    return subPart || String(entry.companyIntelligenceType || 'Observation');
  }

  intelligenceNotesText(entry: any): string {
    if (!entry || entry.notes == null) {
      return '';
    }
    const t = String(entry.notes).trim();
    return t;
  }

  intelligenceDetailLine(entry: any): string {
    if (!entry) {
      return '';
    }
    const parts: string[] = [];
    const v1 = entry.notesValue1;
    if (v1 != null && String(v1).trim() !== '') {
      const n = Number(v1);
      if (!Number.isNaN(n)) {
        parts.push(`Belopp: ${n.toLocaleString('sv-SE')} kr`);
      } else {
        parts.push(`Värde: ${v1}`);
      }
    }
    const v2 = entry.notesValue2;
    if (v2 != null && String(v2).trim() !== '') {
      parts.push(`Antal: ${v2}`);
    }
    if (entry.score != null && typeof entry.score === 'number') {
      parts.push(`Riskpoäng: ${entry.score}`);
    }
    if (entry.firstSeenAt != null && typeof entry.firstSeenAt === 'number') {
      parts.push(`Först sedd ${this.formatUnixSec(entry.firstSeenAt)}`);
    }
    return parts.join(' · ');
  }

  /** When there is no documentTitle (older filings). */
  documentTitleFallback(row: any): string {
    if (!row) {
      return '';
    }
    if (row.documentValue2 && !row.documentTitle) {
      return row.documentValue1
        ? `${row.documentValue1} – ${row.documentValue2}`
        : String(row.documentValue2);
    }
    return '';
  }

  documentMetaLine(row: any): string {
    if (!row) {
      return '';
    }
    const parts: string[] = [];
    if (row.documentVersion) {
      parts.push(`Version ${row.documentVersion}`);
    }
    if (row.documentValue3) {
      parts.push(row.documentValue3);
    }
    if (row.companyDocumentId != null) {
      parts.push(`Dokument-ID ${row.companyDocumentId}`);
    }
    return parts.join(' · ');
  }

  beneficialDetail(bo: any): string {
    if (!bo) {
      return '';
    }
    if (bo.throughName) {
      return `${bo.governDescription || ''} (${bo.throughName})`.trim();
    }
    return bo.governDescription || '';
  }

  financialFileIds(url: string | undefined) {
    return financialFileIdsFromUrl(url);
  }

  async loadTic() {
    if (!this.overview) {
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.ticDocuments = [];
    this.companyId = null;
    this.ticDocument = null;

    try {
      const search = await ticSearchCompany(this.orgNr());
      const id = extractTicCompanyId(search);
      if (id == null) {
        this.loadError =
          'Hittade inget TIC-bolag för detta organisationsnummer.';
        return;
      }
      this.companyId = id;
      this.ticDocument = extractTicDocument(search);
      if (!this.ticDocument) {
        this.loadError =
          'TIC-svaret saknade företagsdata. Försök igen senare.';
        return;
      }

      const rawDocs = Array.isArray(this.ticDocument.documents)
        ? this.ticDocument.documents.filter(
            (d: any) => d && typeof d.companyDocumentType === 'string',
          )
        : [];
      this.ticDocuments = rawDocs.slice().sort((a: any, b: any) => {
        const da = typeof a.documentDate === 'number' ? a.documentDate : 0;
        const db = typeof b.documentDate === 'number' ? b.documentDate : 0;
        return db - da;
      });
    } catch {
      this.loadError =
        'Kunde inte ladda TIC-data. Försök igen senare.';
    } finally {
      this.loading = false;
    }
  }

  async downloadReport(row: any) {
    const ids = financialFileIdsFromUrl(row && row.documentUrl);
    if (!ids || this.companyId == null) {
      return;
    }

    user.setFetchingData(true);
    user.setFetchingMessage('Hämtar PDF…');

    try {
      const buf = await ticGetFinancialReportFile(
        this.companyId,
        ids.summaryId,
        ids.fileId,
      );
      user.setFetchingData(false);
      if (!buf) {
        return;
      }
      const name =
        row && row.companyDocumentId != null
          ? `tic-doc-${row.companyDocumentId}.pdf`
          : `tic-${ids.summaryId}-${ids.fileId}.pdf`;
      const data = new Blob([buf], { type: 'application/pdf' });
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(data, name);
        return;
      }
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
    } catch (e) {
      user.setFetchingData(false);
      console.log(e);
    }
  }
}
